import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import './TicketTableDashboard.css';
import './UI/SearchInput.css';
import { useTranslation } from 'react-i18next';
import { FaInstagram, FaFacebookMessenger, FaWhatsapp, FaEnvelope, FaGlobe, FaSort, FaSortUp, FaSortDown, FaUser, FaPlusCircle, FaSearch, FaDownload, FaExclamationTriangle, FaClipboardList } from 'react-icons/fa';
import { ALL_STATUS_MAPPING, EDN_STATUS_MAPPING, getStatusLabel as getCanonicalStatusLabel, getStatusClass } from '../constants/ticketStatuses';
import TicketExportModal from './TicketExportModal';
import DisplayPreferences from './DisplayPreferences';
import TicketSummaryOverlay from './TicketSummaryOverlay';
import { fetchPreferences, orderFor, ORDER_ALL } from '../utils/preferences';
import { assigneeName, assignmentTooltipText } from '../utils/assignmentReason';

const getStoredUserData = () => {
  try {
    const storedUserData = localStorage.getItem('userData') || localStorage.getItem('user_data');
    return storedUserData ? JSON.parse(storedUserData) : null;
  } catch (error) {
    return null;
  }
};

const EDN_STATUS_OPTIONS = EDN_STATUS_MAPPING.map(({ value, name }) => ({ value, label: name }));

const TicketTableDashboard = ({ 
    tickets, 
    assignedTickets, 
    user, 
    onTicketClick, 
    compact = false, 
    hasFaAccount = false, 
    onCreateTicket, 
    brands = [], 
    selectedBrand = null, 
    onBrandChange = null, 
    pagination, 
    assignedPagination,
    onPageChange,
    onFilterChange,
    onSortChange = null,
    currentViewMode = 'unassigned',
    onSearchStateChange = null,
    ticketStats = null,
    attentionReason = null,
    onAttentionReasonChange = null,
    onRefresh = null,
    onNotify = null,
    isLoading = false
}) => {

  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const storedUserData = getStoredUserData();
  const isEdnCustomUser = user?.custom_user === 'edn'
    || user?.user_data?.custom_user === 'edn'
    || storedUserData?.custom_user === 'edn';

  // Initialize state from URL parameters
  const [sortOrder, setSortOrder] = useState(searchParams.get('sortOrder') || 'desc');
  const [sortField, setSortField] = useState(searchParams.get('sortField') || 'updated_at');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('statusFilter') || '');
  const [ednStatusFilters, setEdnStatusFilters] = useState(searchParams.getAll('statusFilter').filter(Boolean));
  const [ednAdminFilter, setEdnAdminFilter] = useState(searchParams.get('ednAdminFilter') || '');
  const [searchFilter, setSearchFilter] = useState(searchParams.get('searchFilter') || '');
  const [ticketViews, setTicketViews] = useState([]);
  const [activeViewId, setActiveViewId] = useState(null);
  // The person's own display preferences: default view, order per status (28/10).
  const [preferences, setPreferences] = useState(null);
  const [showEdnStatusDropdown, setShowEdnStatusDropdown] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  // Bulk selection + grouped/hover actions (assign, close)
  const [selectedIds, setSelectedIds] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [employeesLoaded, setEmployeesLoaded] = useState(false);
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  // 'bulk' | a ticket id | null — which assign menu is open
  const [assignMenuFor, setAssignMenuFor] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const ednStatusDropdownRef = useRef(null);
  const filterMenuRef = useRef(null);
  const assignMenuRef = useRef(null);

  // Ticket export is a tenant-owner capability: hidden for employees
  // (the backend enforces 403 regardless).
  const exportRole = user?.role || user?.user_data?.role || storedUserData?.role;
  const canExportTickets = Boolean(exportRole) && !['employee', 'experience'].includes(exportRole);
  
  // Set default active tab based on user type, but check URL first
  const getDefaultActiveTab = () => {
    const urlTab = searchParams.get('activeTab');
    if (urlTab) return urlTab;

    return 'General';
  };
  
  const [activeTopicTab, setActiveTopicTab] = useState(getDefaultActiveTab());
  const [searchResults, setSearchResults] = useState(null);
  // Paged search (KI-111): the backend answers page/limit + total, so a status
  // filter matching 593 tickets pages through them instead of stopping at 200.
  const [searchPagination, setSearchPagination] = useState(null);
  const lastSearchCriteriaRef = useRef(null);
  const SEARCH_PAGE_SIZE = 100;
  const [hasSyncedInitialTab, setHasSyncedInitialTab] = useState(false);

  const ticketsPerPage = 20;

  const ASSIGNED_TAB = 'Assigned';
  const MY_ASSIGNED_TAB = 'Assigned to me';
  const GENERAL_TAB = 'General';
  const CHATBOT_TAB = 'chatbot';
  const VIP_TAB = 'VIP';

  const getViewId = (view) => view?.id || view?._id || view?.view_id;
  const buildViewTabKey = (viewId) => `view:${viewId}`;
  const isViewActive = Boolean(activeViewId || (activeTopicTab && activeTopicTab.startsWith('view:')));
  // The attention chips count tickets across BOTH lists (the backend applies
  // the attention_reason filter to the unassigned AND assigned queries), so
  // the facet view merges them exactly like a saved view — otherwise « 18 »
  // on the chip showed 2 rows because 16 were assigned (KI-138).
  const isMergedView = isViewActive || Boolean(attentionReason);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ednStatusDropdownRef.current && !ednStatusDropdownRef.current.contains(event.target)) {
        setShowEdnStatusDropdown(false);
      }
      if (filterMenuRef.current && !filterMenuRef.current.contains(event.target)) {
        setShowFilterMenu(false);
      }
      if (assignMenuRef.current && !assignMenuRef.current.contains(event.target)) {
        setAssignMenuFor(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Selection only ever references rows that are on screen.
  useEffect(() => {
    setSelectedIds((prev) => {
      if (!prev.length) return prev;
      const visible = new Set([
        ...(tickets || []).map((item) => item._id),
        ...(assignedTickets || []).map((item) => item._id),
        ...((searchResults || []).map((item) => item._id)),
      ]);
      const kept = prev.filter((id) => visible.has(id));
      return kept.length === prev.length ? prev : kept;
    });
  }, [tickets, assignedTickets, searchResults]);

  useEffect(() => {
    let isSubscribed = true;

    const fetchTicketViews = async () => {
      const token = localStorage.getItem('access_token');
      if (!token) {
        if (isSubscribed) {
          setTicketViews([]);
          setActiveViewId(null);
        }
        return;
      }

      try {
        const response = await axios.get('/api/tickets/ticket-views', {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (!isSubscribed) {
          return;
        }

        const viewsData = Array.isArray(response.data) ? response.data : [];
        setTicketViews(viewsData);

        const activeTabParam = searchParams.get('activeTab');
        if (activeTabParam && activeTabParam.startsWith('view:')) {
          const viewIdFromParam = activeTabParam.replace('view:', '');
          const matchedView = viewsData.find((view) => String(getViewId(view)) === viewIdFromParam);
          if (matchedView) {
            // Notify filters: restoring a view without its query showed the
            // Général data under the view's name (browser-back / deep link).
            handleViewTabChange(matchedView, { skipUrlUpdate: true, suppressSyncFlag: true });
            return;
          }
        }

        if (viewsData.length === 0) {
          setActiveViewId(null);
          if (activeTopicTab && activeTopicTab.startsWith('view:')) {
            handleTopicTabChange(GENERAL_TAB);
          }
          return;
        }

        if (activeTopicTab && activeTopicTab.startsWith('view:')) {
          const currentViewId = activeTopicTab.replace('view:', '');
          const stillExists = viewsData.some((view) => String(getViewId(view)) === currentViewId);
          if (stillExists) {
            setActiveViewId(currentViewId);
            return;
          }
          // A view deleted since (e.g. someone's default view): back to Général.
          handleTopicTabChange(GENERAL_TAB);
          return;
        }

      } catch (error) {
        console.error('Failed to fetch ticket views:', error);
        if (isSubscribed) {
          setTicketViews([]);
          setActiveViewId(null);
          if (activeTopicTab && activeTopicTab.startsWith('view:')) {
            handleTopicTabChange(GENERAL_TAB);
          }
        }
      }
    };

    fetchTicketViews();

    return () => {
      isSubscribed = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?._id]);

  useEffect(() => {
    let alive = true;
    fetchPreferences().then((loaded) => { if (alive) setPreferences(loaded); });
    return () => { alive = false; };
  }, []);

  const channelIcons = {
    Instagram: FaInstagram,
    Messenger: FaFacebookMessenger,
    WhatsApp: FaWhatsapp,
    Gmail: FaEnvelope,
    Outlook: FaEnvelope,
    Website: FaGlobe,
    Custom: FaEnvelope
  };

  const formatDate = (dateString) => {
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric', 
      hour: '2-digit', 
      minute: '2-digit',
      timeZone: 'Europe/Paris'
    };
    // Add 'Z' to indicate UTC if not already present
    const utcDateString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
    return new Date(utcDateString).toLocaleDateString('fr-FR', options);
  };

  const getInitials = (name) => {
    const words = String(name).replace(/@.*/, '').split(/[\s._-]+/).filter(Boolean);
    if (!words.length) return '?';
    return words.slice(0, 2).map((word) => word[0].toUpperCase()).join('');
  };

  const sortTickets = (tickets, field, order) => {
    return [...tickets].sort((a, b) => {
      const valA = a[field];
      const valB = b[field];
      if (field === 'updated_at' || field === 'created_at') {
        return order === 'asc' ? new Date(valA) - new Date(valB) : new Date(valB) - new Date(valA);
      }
      if (field === 'sentiment_score') {
        // Missing scores always sink to the bottom, whatever the direction
        const numA = Number.isFinite(Number(valA)) ? Number(valA) : (order === 'asc' ? Infinity : -Infinity);
        const numB = Number.isFinite(Number(valB)) ? Number(valB) : (order === 'asc' ? Infinity : -Infinity);
        return order === 'asc' ? numA - numB : numB - numA;
      }
      const strA = String(valA ?? '');
      const strB = String(valB ?? '');
      return order === 'asc' ? strA.localeCompare(strB) : strB.localeCompare(strA);
    });
  };

  const filterTickets = (tickets) => {
    // Backend now handles topic and assigned separation
    // Only handle frontend filtering for search results and brand
    
    // Brand filtering (still frontend until backend supports it). Brand ids
    // are ObjectId strings — compare as strings so a numeric-looking id can
    // never silently disable the filter. Tickets without a brand_id pass
    // (hiding them would empty every brand view of unmapped tickets).
    const brandMatch = (ticket) => !selectedBrand
      || !ticket.brand_id
      || String(ticket.brand_id) === String(selectedBrand);

    if (searchResults !== null) {
      return searchResults.filter(brandMatch);
    }

    return tickets.filter(brandMatch);
  };

  // Sorting is server-side (KI-111): the parent re-queries page 1 with the
  // new order, and an active search re-runs with it too.
  const applySort = (field, order) => {
    setSortField(field);
    setSortOrder(order);
    updateUrlParams({ sortField: field, sortOrder: order });
    if (onSortChange) {
      onSortChange({ sort_by: field, order });
    }
    if (searchResults !== null && lastSearchCriteriaRef.current) {
      performSearch(lastSearchCriteriaRef.current, { page: 1, sortBy: field, order });
    }
  };

  const handleSortChange = (field) => {
    const newSortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    applySort(field, newSortOrder);
  };

  const handleEdnStatusToggle = (statusValue) => {
    const selectedStatuses = ednStatusFilters.includes(statusValue)
      ? ednStatusFilters.filter((status) => status !== statusValue)
      : [...ednStatusFilters, statusValue];

    const sort = preferredSort(selectedStatuses.length === 1 ? selectedStatuses[0] : ORDER_ALL);
    setEdnStatusFilters(selectedStatuses);
    setSortField(sort.field);
    setSortOrder(sort.order);
    updateUrlParams({ statusFilter: selectedStatuses, sortField: sort.field, sortOrder: sort.order });
    performSearch({ query: searchFilter, ticket_status: selectedStatuses }, { sortBy: sort.field, order: sort.order });
    syncParentSort(sort, selectedStatuses.length === 0);
  };

  // Simplified search function that only uses query parameter.
  // options: { page, sortBy, order } — paging and sort are server-side.
  const performSearch = async (searchData = null, options = {}) => {
    const page = options.page || 1;
    const sortBy = options.sortBy || sortField;
    const order = options.order || sortOrder;
    const searchCriteria = searchData || {
      query: searchFilter,
      ticket_status: isEdnCustomUser ? ednStatusFilters : statusFilter
    };

    if (isEdnCustomUser) {
      if (!Object.prototype.hasOwnProperty.call(searchCriteria, 'ticket_status') && ednStatusFilters.length > 0) {
        searchCriteria.ticket_status = ednStatusFilters;
      }
      const hasAdminFilter = Object.prototype.hasOwnProperty.call(searchCriteria, 'affected_admin_name')
        || Object.prototype.hasOwnProperty.call(searchCriteria, 'affected_admin_email');
      if (!hasAdminFilter) {
        const trimmedAdminFilter = ednAdminFilter.trim();
        if (trimmedAdminFilter) {
          if (trimmedAdminFilter.includes('@')) {
            searchCriteria.affected_admin_email = trimmedAdminFilter;
          } else {
            searchCriteria.affected_admin_name = trimmedAdminFilter;
          }
        }
      }
    }

    // Remove empty search criteria
    const filteredCriteria = Object.entries(searchCriteria)
      .filter(([key, value]) => {
        if (Array.isArray(value)) {
          return value.length > 0;
        }
        return value && value.trim() !== '';
      })
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

    if (Object.keys(filteredCriteria).length === 0) {
      setSearchResults(null);
      setSearchPagination(null);
      lastSearchCriteriaRef.current = null;
      // Notify parent that search is inactive
      if (onSearchStateChange) {
        onSearchStateChange(false);
      }
      return;
    }

    lastSearchCriteriaRef.current = filteredCriteria;

    try {
      const token = localStorage.getItem('access_token');
      const queryParams = new URLSearchParams();
      Object.entries(filteredCriteria).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((item) => queryParams.append(key, item));
          return;
        }
        queryParams.append(key, value);
      });
      queryParams.append('page', String(page));
      queryParams.append('limit', String(SEARCH_PAGE_SIZE));
      queryParams.append('sort_by', sortBy);
      queryParams.append('order', order);
      const response = await axios.get(`/api/tickets/tickets/search?${queryParams}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearchResults(response.data.tickets || []);
      setSearchPagination(response.data.pagination || null);
      // Notify parent that search is active
      if (onSearchStateChange) {
        onSearchStateChange(true);
      }
    } catch (error) {
      console.error('Error searching tickets:', error);
      setSearchResults([]);
      setSearchPagination(null);
      // Still notify that search is active even if failed
      if (onSearchStateChange) {
        onSearchStateChange(true);
      }
    }
  };

  const handleSearchFilterChange = (e) => {
    const searchTerm = e.target.value;
    setSearchFilter(searchTerm);
    updateUrlParams({ searchFilter: searchTerm });

    // Clear search results if input is empty
    if (searchTerm.trim() === '') {
      setSearchResults(null);
      setSearchPagination(null);
      lastSearchCriteriaRef.current = null;
      // Notify parent that search is inactive
      if (onSearchStateChange) {
        onSearchStateChange(false);
      }
    }
  };

  const handleEdnAdminFilterChange = (e) => {
    const adminFilter = e.target.value;
    setEdnAdminFilter(adminFilter);
    updateUrlParams({ ednAdminFilter: adminFilter });
  };

  const handleEdnAdminFilterSubmit = () => {
    const trimmedAdminFilter = ednAdminFilter.trim();
    performSearch({
      query: searchFilter,
      ticket_status: ednStatusFilters,
      affected_admin_name: trimmedAdminFilter && !trimmedAdminFilter.includes('@') ? trimmedAdminFilter : '',
      affected_admin_email: trimmedAdminFilter && trimmedAdminFilter.includes('@') ? trimmedAdminFilter : ''
    });
  };

  const handleEdnAdminFilterKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleEdnAdminFilterSubmit();
    }
  };

  const clearEdnStatusFilters = () => {
    setEdnStatusFilters([]);
    updateUrlParams({ statusFilter: [] });
    performSearch({ query: searchFilter, ticket_status: [] });
  };

  // Handle search submission
  const handleSearchSubmit = async () => {
    if (searchFilter.trim() === '') {
      if (isEdnCustomUser && (ednStatusFilters.length > 0 || ednAdminFilter.trim())) {
        performSearch({
          ticket_status: ednStatusFilters
        });
        return;
      }
      setSearchResults(null);
      if (onSearchStateChange) {
        onSearchStateChange(false);
      }
      return;
    }
    
    await performSearch({
      query: searchFilter,
      ticket_status: isEdnCustomUser ? ednStatusFilters : statusFilter
    });
  };

  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearchSubmit();
    }
  };

  const clearSearchFilter = () => {
    setSearchFilter('');
    setSearchResults(null);
    
    // Notify parent that search is inactive
    if (onSearchStateChange) {
      onSearchStateChange(false);
    }
    
    // Clear URL params
    updateUrlParams({ searchFilter: '' });
  };

  const clearEdnAdminFilter = () => {
    setEdnAdminFilter('');
    updateUrlParams({ ednAdminFilter: '' });
    performSearch({
      query: searchFilter,
      ticket_status: ednStatusFilters,
      affected_admin_name: '',
      affected_admin_email: ''
    });
  };

  // Thresholds calibrated on the real score distribution (mostly 30–70):
  // ≤39 reads as a problem, 40–65 as neutral, >65 as good.
  const getSentimentScoreClass = (score) => {
    if (!Number.isFinite(Number(score))) return 'empty';
    if (score <= 39) return 'low';
    if (score <= 65) return 'medium';
    return 'high';
  };

  // KPI tiles act as filter shortcuts: click filters the table on that status,
  // click again clears it.
  const activeKpiStatus = isEdnCustomUser
    ? (ednStatusFilters.length === 1 ? ednStatusFilters[0] : null)
    : (statusFilter || null);

  const applyKpiStatusFilter = (statusValue) => {
    const isActive = activeKpiStatus === statusValue;
    if (isEdnCustomUser) {
      const next = isActive ? [] : [statusValue];
      const sort = preferredSort(next.length === 1 ? next[0] : ORDER_ALL);
      setEdnStatusFilters(next);
      setSortField(sort.field);
      setSortOrder(sort.order);
      updateUrlParams({ statusFilter: next, sortField: sort.field, sortOrder: sort.order });
      performSearch({ query: searchFilter, ticket_status: next }, { sortBy: sort.field, order: sort.order });
      syncParentSort(sort, next.length === 0);
    } else {
      const next = isActive ? '' : statusValue;
      const sort = preferredSort(next || ORDER_ALL);
      setStatusFilter(next);
      setSortField(sort.field);
      setSortOrder(sort.order);
      updateUrlParams({ statusFilter: next, sortField: sort.field, sortOrder: sort.order });
      performSearch({ query: searchFilter, ticket_status: next }, { sortBy: sort.field, order: sort.order });
      syncParentSort(sort, !next);
    }
  };

  // The order the person chose for a status (or ORDER_ALL), most recently
  // updated first by default; the current sort until the preferences load.
  const preferredSort = (statusKey) => (preferences
    ? { field: 'updated_at', order: orderFor(preferences, statusKey) }
    : { field: sortField, order: sortOrder });

  // Back on the unfiltered lists, the parent re-queries them in that order.
  const syncParentSort = (sort, filterCleared) => {
    if (filterCleared && onSortChange && (sort.field !== sortField || sort.order !== sortOrder)) {
      onSortChange({ sort_by: sort.field, order: sort.order });
    }
  };

  const currentOrderKey = () => {
    if (isEdnCustomUser) return ednStatusFilters.length === 1 ? ednStatusFilters[0] : ORDER_ALL;
    return statusFilter || ORDER_ALL;
  };

  // A preference just saved in « Affichage »: an order changed for what the
  // list shows now applies at once.
  const handlePreferencesSaved = (saved, update) => {
    setPreferences(saved);
    const changed = Object.keys(update?.ticket_order || {});
    if (changed.length === 0) return;
    const key = currentOrderKey();
    const ownOrder = (saved.ticket_order || {})[key];
    if (changed.includes(key) || (changed.includes(ORDER_ALL) && !ownOrder)) {
      const order = orderFor(saved, key);
      if (order !== sortOrder || sortField !== 'updated_at') applySort('updated_at', order);
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <FaSort />;
    return sortOrder === 'asc' ? <FaSortUp /> : <FaSortDown />;
  };

  const getPresenceViewers = (ticket) => {
    if (!ticket?.presence_viewers) {
      return [];
    }
    return ticket.presence_viewers.filter((viewer) => viewer && viewer.viewer_id);
  };

  const getPresenceTooltip = (viewers) => {
    if (!viewers.length) {
      return '';
    }
    const viewerNames = viewers.map((viewer) => viewer.viewer_name || viewer.role || t('unknown', 'Unknown'));
    const viewersList = viewerNames.join('\n');
    return `${t('ticketViewedByLabel', 'Currently viewed by')}:\n${viewersList}`;
  };

  const getTicketTopicLabel = (ticket) => {
    return ticket?.ticket_topic?.topic_full
      || ticket?.ticket_topic?.name
      || ticket?.topic_name
      || ticket?.metadata?.topic_full
      || ticket?.topic_category
      || ticket?.metadata?.topic
      || ticket?.topic
      || t('unknown', 'Unknown');
  };

  const getAffectedAdminLabel = (ticket) => {
    return ticket?.metadata?.affected_admin_name
      || ticket?.affected_admin_name
      || ticket?.metadata?.affected_admin_email
      || ticket?.affected_admin_email
      || '-';
  };

  const getStatusLabel = (status) => {
    if (isEdnCustomUser) {
      return EDN_STATUS_OPTIONS.find((option) => option.value === status)?.label
        || getCanonicalStatusLabel(t, status);
    }

    return getCanonicalStatusLabel(t, status);
  };

  const getEdnStatusFilterLabel = () => {
    if (ednStatusFilters.length === 0) {
      return t('allStatuses', 'All statuses');
    }

    if (ednStatusFilters.length === 1) {
      return EDN_STATUS_OPTIONS.find((option) => option.value === ednStatusFilters[0])?.label || ednStatusFilters[0];
    }

    return t('selectedStatusesCount', '{{count}} statuses selected', { count: ednStatusFilters.length });
  };

  // Fixed-position tooltip: the table lives in an overflow-x container which
  // clips absolutely-positioned descendants — the old CSS ::after tooltip was
  // trapped inside the table (user report 2026-10-02). position:fixed escapes
  // every overflow context (no transformed ancestor on this page).
  const [presenceTooltip, setPresenceTooltip] = useState(null);
  // The ticket whose « Résumé » overlay is open ({ id, subject }) — 28/10.
  const [summaryFor, setSummaryFor] = useState(null);

  const renderPresenceIndicator = (ticket) => {
    const viewers = getPresenceViewers(ticket);
    if (!viewers.length) {
      return null;
    }

    const tooltip = getPresenceTooltip(viewers);

    const showTooltip = (event) => {
      const rect = event.currentTarget.getBoundingClientRect();
      setPresenceTooltip({ text: tooltip, x: rect.right, y: rect.bottom + 6 });
    };
    const hideTooltip = () => setPresenceTooltip(null);

    return (
      <span
        className="presence-indicator"
        onClick={(event) => event.stopPropagation()}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onFocus={showTooltip}
        onBlur={hideTooltip}
        tabIndex={0}
      >
        <FaUser className="presence-icon" />
        <span className="presence-count">{viewers.length}</span>
      </span>
    );
  };

  // Status chip of an assigned ticket + the assignee's initials: hovering
  // either shows who it is assigned to, by whom, when and why (28/10), in
  // the same fixed-position tooltip as the presence indicator.
  const renderStatusCell = (ticket, statusLabel) => {
    const chip = (
      <span className={`status-chip ${getStatusClass(ticket.status)}`} title={ticket.assignment_reason ? undefined : statusLabel}>
        <span className="status-chip-dot" aria-hidden="true" />
        <span className="status-chip-label">{statusLabel}</span>
      </span>
    );
    if (!ticket.assignment_reason) {
      return chip;
    }
    const text = assignmentTooltipText(t, ticket);
    const name = assigneeName(ticket);
    const showTooltip = (event) => {
      const rect = event.currentTarget.getBoundingClientRect();
      setPresenceTooltip({ text, x: rect.left, y: rect.bottom + 6, align: 'start' });
    };
    const hideTooltip = () => setPresenceTooltip(null);
    return (
      <span
        className="status-assignee"
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onFocus={showTooltip}
        onBlur={hideTooltip}
        tabIndex={0}
        aria-label={`${statusLabel} · ${text}`}
        data-testid="status-assignee"
      >
        {chip}
        <span className="assignee-marker" aria-hidden="true">
          {name ? getInitials(name) : <FaUser />}
        </span>
      </span>
    );
  };

  useEffect(() => {
    if (hasSyncedInitialTab) {
      return;
    }

    if (!activeTopicTab) {
      return;
    }

    if (activeTopicTab.startsWith('view:')) {
      if (!ticketViews.length) {
        return;
      }

      const currentViewId = activeTopicTab.replace('view:', '');
      const matchedView = ticketViews.find((view) => String(getViewId(view)) === currentViewId);

      if (matchedView) {
        handleViewTabChange(matchedView, { skipUrlUpdate: true });
      } else {
        handleTopicTabChange(GENERAL_TAB, { skipUrlUpdate: true });
      }

      return;
    }

    handleTopicTabChange(activeTopicTab, { skipUrlUpdate: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTopicTab, ticketViews, hasSyncedInitialTab]);

  const buildTopicFilters = (topic) => {
    if (topic === ASSIGNED_TAB) {
      return {
        topics: [],
        separate_assigned: true,
        viewMode: 'assigned',
        viewId: null,
        is_vip_only: false,
        // Explicitly clear view-specific filters
        statuses_to_exclude: [],
        resolution_actors_to_exclude: [],
        channel_ids: [],
        employee_ids: []
      };
    }

    if (topic === MY_ASSIGNED_TAB) {
      return {
        topics: [],
        separate_assigned: true,
        viewMode: 'my_assigned',
        viewId: null,
        is_vip_only: false,
        // Explicitly clear view-specific filters
        statuses_to_exclude: [],
        resolution_actors_to_exclude: [],
        channel_ids: [],
        employee_ids: []
      };
    }

    if (topic === CHATBOT_TAB) {
      return {
        topics: ['chatbot'],
        separate_assigned: true,
        viewMode: 'unassigned',
        viewId: null,
        is_vip_only: false,
        // Explicitly clear view-specific filters
        statuses_to_exclude: [],
        resolution_actors_to_exclude: [],
        channel_ids: [],
        employee_ids: []
      };
    }

    if (topic === VIP_TAB) {
      return {
        topics: [],
        separate_assigned: true,
        viewMode: 'unassigned',
        viewId: null,
        is_vip_only: true,
        // Explicitly clear view-specific filters
        statuses_to_exclude: [],
        resolution_actors_to_exclude: [],
        channel_ids: [],
        employee_ids: []
      };
    }

    return {
      topics: topic === GENERAL_TAB ? [] : [topic],
      separate_assigned: true,
      viewMode: 'unassigned',
      viewId: null,
      is_vip_only: false,
      // Explicitly clear view-specific filters
      statuses_to_exclude: [],
      resolution_actors_to_exclude: [],
      channel_ids: [],
      employee_ids: []
    };
  };

  const notifyFiltersForTopic = (topic, options = {}) => {
    if (!onFilterChange || options.skipFilterNotification) {
      return;
    }

    onFilterChange(buildTopicFilters(topic));
  };

  const buildViewFilters = (view) => {
    const viewId = getViewId(view);
    const viewFilters = view?.filters || {};

    return {
      separate_assigned: true,
      viewMode: 'view',
      viewId,
      topics: Array.isArray(viewFilters.topics) ? viewFilters.topics : [],
      statuses_to_exclude: Array.isArray(viewFilters.statuses_to_exclude) ? viewFilters.statuses_to_exclude : [],
      resolution_actors_to_exclude: Array.isArray(viewFilters.resolution_actors_to_exclude)
        ? viewFilters.resolution_actors_to_exclude
        : [],
      channel_ids: Array.isArray(viewFilters.channel_ids) ? viewFilters.channel_ids : [],
      employee_ids: Array.isArray(viewFilters.employee_ids) ? viewFilters.employee_ids : []
    };
  };

  const notifyFiltersForView = (view, options = {}) => {
    if (!onFilterChange || options.skipFilterNotification) {
      return;
    }

    onFilterChange(buildViewFilters(view));
  };

  const handleTopicTabChange = (topic, options = {}) => {
    setActiveTopicTab(topic);
    setActiveViewId(null);

    if (!options.skipUrlUpdate) {
      updateUrlParams({ activeTab: topic });
    }

    if (!options.suppressSyncFlag) {
      setHasSyncedInitialTab(true);
    }

    notifyFiltersForTopic(topic, options);
  };

  const handleViewTabChange = (view, options = {}) => {
    const viewId = getViewId(view);
    if (!viewId) {
      console.warn('Skipping view tab activation: missing identifier', view);
      return;
    }

    const tabKey = buildViewTabKey(viewId);
    setActiveTopicTab(tabKey);
    setActiveViewId(String(viewId));

    if (!options.skipUrlUpdate) {
      updateUrlParams({ activeTab: tabKey });
    }

    if (!options.suppressSyncFlag) {
      setHasSyncedInitialTab(true);
    }

    notifyFiltersForView(view, options);
  };

  const handleBrandChange = (brandId) => {
    if (onBrandChange) {
      onBrandChange(brandId);
    }
  };

  // Get the current tickets based on view mode
  const getCurrentTickets = () => {
    // If a view or the attention facet is active, merge both lists
    if (isMergedView) {
      const unassigned = tickets || [];
      const assigned = assignedTickets || [];
      return [...unassigned, ...assigned];
    }
    
    if (currentViewMode === 'assigned') {
      return assignedTickets || [];
    } else if (currentViewMode === 'my_assigned') {
      return assignedTickets || []; // my_assigned uses assignedTickets slot
    } else {
      return tickets || [];
    }
  };

  // Get the current pagination based on view mode
  const getCurrentPagination = () => {
    if (isMergedView) {
      const currentPage = Math.max(
        pagination?.current_page ?? 0,
        assignedPagination?.current_page ?? 0,
        1
      );

      const totalPages = Math.max(
        pagination?.total_pages ?? 0,
        assignedPagination?.total_pages ?? 0,
        1
      );

      return {
        current_page: currentPage,
        total_pages: totalPages,
        has_next: Boolean(pagination?.has_next || assignedPagination?.has_next),
        has_previous: Boolean(pagination?.has_previous || assignedPagination?.has_previous)
      };
    }

    if (currentViewMode === 'assigned' || currentViewMode === 'my_assigned') {
      return assignedPagination;
    }

    return pagination;
  };

  const displayedTickets = searchResults !== null ? searchResults : getCurrentTickets();
  // Rows arrive in server order (KI-111). The one exception is a saved view,
  // which merges the unassigned and assigned pages client-side: re-sorting the
  // merged array keeps that page coherent, but a view is only sorted per page
  // (two independently paged lists cannot be globally interleaved).
  const filteredTickets = filterTickets(displayedTickets);
  const sortedAndFilteredTickets = isMergedView && searchResults === null
    ? sortTickets(filteredTickets, sortField, sortOrder)
    : filteredTickets;

  const handlePageChange = (direction) => {
    if (searchResults !== null) {
      const current = searchPagination?.page || 1;
      if (direction === 'next' && searchPagination?.has_next) {
        performSearch(lastSearchCriteriaRef.current, { page: current + 1 });
      } else if (direction === 'prev' && searchPagination?.has_previous) {
        performSearch(lastSearchCriteriaRef.current, { page: Math.max(1, current - 1) });
      }
      return;
    }

    if (isMergedView) {
      const currentUnassigned = pagination?.current_page ?? 1;
      const currentAssigned = assignedPagination?.current_page ?? 1;

      const nextUnassigned = pagination
        ? direction === 'next'
          ? (pagination.has_next ? currentUnassigned + 1 : currentUnassigned)
          : (direction === 'prev' && pagination.has_previous ? Math.max(1, currentUnassigned - 1) : currentUnassigned)
        : null;

      const nextAssigned = assignedPagination
        ? direction === 'next'
          ? (assignedPagination.has_next ? currentAssigned + 1 : currentAssigned)
          : (direction === 'prev' && assignedPagination.has_previous ? Math.max(1, currentAssigned - 1) : currentAssigned)
        : null;

      const unassignedChanged = pagination ? nextUnassigned !== currentUnassigned : false;
      const assignedChanged = assignedPagination ? nextAssigned !== currentAssigned : false;

      if (unassignedChanged || assignedChanged) {
        onPageChange(
          unassignedChanged ? nextUnassigned : null,
          assignedChanged ? nextAssigned : null
        );
      }
      return;
    }

    if (currentViewMode === 'assigned' || currentViewMode === 'my_assigned') {
      const currentAssignedPage = assignedPagination?.current_page || 1;

      if (direction === 'next' && assignedPagination?.has_next) {
        onPageChange(null, currentAssignedPage + 1);
      } else if (direction === 'prev' && assignedPagination?.has_previous) {
        onPageChange(null, Math.max(1, currentAssignedPage - 1));
      }
      return;
    }

    const currentUnassignedPage = pagination?.current_page || 1;

    if (direction === 'next' && pagination?.has_next) {
      onPageChange(currentUnassignedPage + 1, null);
    } else if (direction === 'prev' && pagination?.has_previous) {
      onPageChange(Math.max(1, currentUnassignedPage - 1), null);
    }
  };

  // Update URL parameters when filters change
  const updateUrlParams = (updates) => {
    const newParams = new URLSearchParams(searchParams);
    
    Object.entries(updates).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        newParams.delete(key);
        value.forEach((item) => {
          if (item !== '' && item !== null && item !== undefined) {
            newParams.append(key, item.toString());
          }
        });
        return;
      }

      if (value === '' || value === null || value === undefined) {
        newParams.delete(key);
      } else {
        newParams.set(key, value.toString());
      }
    });
    
    setSearchParams(newParams, { replace: true });
  };

  // Simplified useEffect to handle search on mount
  useEffect(() => {
    const hasSimpleSearchParam = searchParams.get('searchFilter') && searchParams.get('searchFilter').trim() !== '';
    const statusParams = searchParams.getAll('statusFilter').filter(Boolean);
    const hasStatusFilter = statusParams.length > 0;
    const hasEdnAdminFilter = searchParams.get('ednAdminFilter') && searchParams.get('ednAdminFilter').trim() !== '';
    
    if (hasSimpleSearchParam || hasStatusFilter || (isEdnCustomUser && hasEdnAdminFilter)) {
      // Trigger search with URL parameters
      performSearch({
        query: searchParams.get('searchFilter') || '',
        ticket_status: isEdnCustomUser ? statusParams : searchParams.get('statusFilter') || ''
      });
    }
  }, []); // Run only on mount

  const renderPagination = () => {
    if (searchResults !== null) {
      const totalResults = searchPagination?.total_tickets ?? searchResults.length;
      const totalPages = searchPagination?.total_pages || 1;
      return (
        <div className="pagination">
          <span className="pagination-count">
            {t('searchResultsCount', '{{count}} résultats', { count: totalResults })}
          </span>
          {totalPages > 1 && (
            <>
              <button
                onClick={() => handlePageChange('prev')}
                disabled={!searchPagination?.has_previous}
              >
                {t('common.previous')}
              </button>
              <span>
                {searchPagination?.page || 1} / {totalPages}
              </span>
              <button
                onClick={() => handlePageChange('next')}
                disabled={!searchPagination?.has_next}
              >
                {t('common.next')}
              </button>
            </>
          )}
        </div>
      );
    }
    const current = getCurrentPagination();
    const totalTickets = !isViewActive && Number.isFinite(Number(current?.total_tickets))
      ? Number(current.total_tickets)
      : null;
    return (
      <div className="pagination">
        {totalTickets !== null && (
          <span className="pagination-count">
            {t('paginationTickets', '{{count}} tickets', { count: totalTickets.toLocaleString('fr-FR') })}
          </span>
        )}
        <button
          onClick={() => handlePageChange('prev')}
          disabled={!current?.has_previous}
        >
          {t('common.previous')}
        </button>
        <span>
          {t('paginationPageOf', 'Page {{page}} / {{pages}}', {
            page: current?.current_page || 1,
            pages: current?.total_pages || 1
          })}
        </span>
        <button
          onClick={() => handlePageChange('next')}
          disabled={!current?.has_next}
        >
          {t('common.next')}
        </button>
      </div>
    );
  };

  // ── Row helpers (mockup layout) ──────────────────────────────────────────

  // Who holds the next move, from the ticket status (coarse but honest; the
  // precise work state lives in the row's attention flags).
  const holderForStatus = (status) => {
    if (!status) return null;
    if (status === 'waiting_client_response') return 'customer';
    if (['waiting_carrier_response', 'waiting_carrier_commercial_response', 'waiting_carriers_response'].includes(status)) return 'carrier';
    if (['automated', 'resolved', 'ai_resolved', 'resolved_npec', 'closed'].includes(status)) return null;
    return 'team';
  };

  const HOLDER_LABELS = {
    team: t('holderTeam', 'Équipe'),
    customer: t('holderCustomer', 'Client'),
    carrier: t('holderCarrier', 'Transporteur'),
  };

  const formatDurationSince = (dateString) => {
    if (!dateString) return '';
    const utc = dateString.endsWith('Z') ? dateString : dateString + 'Z';
    const mins = Math.max(0, Math.round((Date.now() - new Date(utc).getTime()) / 60000));
    if (mins < 60) return `${mins} min`;
    const hours = Math.floor(mins / 60);
    if (hours < 48) return `${hours} h`;
    return `${Math.floor(hours / 24)} j`;
  };

  // "m.dupont@orange.fr" → "M Dupont": the display name is derived from the
  // email itself (we store no separate customer name), never invented.
  const prettyNameFromEmail = (email) => {
    if (!email) return null;
    const local = String(email).split('@')[0];
    const words = local.split(/[._-]+/).filter(Boolean);
    if (!words.length) return email;
    return words
      .map((word) => (word.length > 1 ? word[0].toUpperCase() + word.slice(1) : word.toUpperCase()))
      .join(' ');
  };

  const clientCellFor = (ticket) => {
    const email = ticket.customer_email || '';
    // In FA mode company_name carries the shop/brand mapping ("France
    // Accessoires", "Unknown"), not a customer identity — never show it there.
    const company = hasFaAccount ? '' : (ticket.company_name || '');
    if (!email && !company) {
      // Chatbot / web-widget visitors have no identity beyond the session.
      return {
        primary: t('chatbotVisitor', 'Visiteur chatbot'),
        secondary: t('sessionShort', 'session #{{id}}', { id: String(ticket._id || '').slice(-4) }),
      };
    }
    if (company) {
      return { primary: company, secondary: email };
    }
    return { primary: prettyNameFromEmail(email), secondary: email };
  };

  // ── Bulk + hover actions (existing single-ticket endpoints, fanned out) ──

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } });

  const ensureEmployees = async () => {
    if (employeesLoaded) return;
    try {
      const response = await axios.get('/api/employees/agents/basic', authHeaders());
      setEmployees(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
      setEmployees([]);
    }
    setEmployeesLoaded(true);
  };

  const notifyResult = (ok, ko) => {
    if (onNotify) onNotify(t('bulkDone', '{{ok}} réussis · {{ko}} échecs', { ok, ko }), ko === 0);
  };

  const runBulk = async (ids, runOne) => {
    setBulkBusy(true);
    const results = await Promise.allSettled(ids.map(runOne));
    const ok = results.filter((result) => result.status === 'fulfilled').length;
    notifyResult(ok, results.length - ok);
    setBulkBusy(false);
    setSelectedIds([]);
    setAssignMenuFor(null);
    if (onRefresh) onRefresh();
  };

  const assignTickets = (ids, employeeId) => runBulk(ids, (ticketId) =>
    axios.post('/api/employees/assign-employee', { employee_id: employeeId, ticket_id: ticketId }, authHeaders()));

  // « Clore » is « Résoudre » since 2026-10-12: a closed ticket is a resolved one.
  const resolveTickets = (ids) => {
    const confirmed = window.confirm(t('bulkResolveConfirm', 'Résoudre {{count}} tickets ? Cette action les retire de la file.', { count: ids.length }));
    if (!confirmed) return;
    runBulk(ids, (ticketId) => axios.put(`/api/tickets/${ticketId}/resolve`, null, authHeaders()));
  };

  const toggleSelected = (ticketId) => {
    setSelectedIds((prev) => (prev.includes(ticketId) ? prev.filter((id) => id !== ticketId) : [...prev, ticketId]));
  };

  const AssignMenu = ({ ids }) => (
    <div className="assign-menu" role="menu">
      {employeesLoaded && employees.length === 0 && (
        <span className="assign-menu-empty">{t('noSearchResults', 'Aucun résultat.')}</span>
      )}
      {employees.map((employee) => {
        const employeeId = employee._id || employee.id;
        return (
          <button
            key={employeeId}
            type="button"
            role="menuitem"
            disabled={bulkBusy}
            onClick={(event) => { event.stopPropagation(); assignTickets(ids, employeeId); }}
          >
            {employee.name || employeeId}
          </button>
        );
      })}
    </div>
  );

  const canAct = user?.role !== 'experience';
  const visibleIds = sortedAndFilteredTickets.map((ticket) => ticket._id);
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));
  const toggleSelectAll = () => {
    setSelectedIds(allSelected ? [] : visibleIds);
  };

  // Active facet chips: one per filter dimension, each removable in place.
  const assignmentFacet = activeTopicTab === ASSIGNED_TAB
    ? t('queueAssigned', 'Assignés')
    : activeTopicTab === MY_ASSIGNED_TAB
      ? t('queueMine', 'À moi')
      : null;

  return (
    <div className={`ticket-table-dashboard${hasFaAccount ? ' fa-mode' : ''}${isEdnCustomUser ? ' edn-mode' : ''}`}>
      {presenceTooltip && (
        <div
          className={`presence-tooltip-fixed${presenceTooltip.align === 'start' ? ' align-start' : ''}`}
          style={{ left: presenceTooltip.x, top: presenceTooltip.y }}
          role="tooltip"
        >
          {presenceTooltip.text}
        </div>
      )}
      {summaryFor && (
        <TicketSummaryOverlay
          ticketId={summaryFor.id}
          subject={summaryFor.subject}
          onClose={() => setSummaryFor(null)}
        />
      )}
      {brands.length > 0 && (
        <div className="una-segmented-tabs brand-tabs" role="tablist" aria-label={t('brands', 'Brands')}>
          {brands.map(brand => (
            <button
              key={brand.id}
              type="button"
              role="tab"
              aria-selected={selectedBrand === brand.id}
              className={`una-segmented-tab brand-tab ${selectedBrand === brand.id ? 'active' : ''}`}
              onClick={() => handleBrandChange(brand.id)}
            >
              <span className="una-segmented-tab-label">{brand.name}</span>
            </button>
          ))}
        </div>
      )}

      <div className="table-card">
      <div className="table-card-header">
      {canAct && (
        <div className="queue-toolbar">
            {ticketViews.length > 0 && (
              <label className="queue-view">
                <span className="queue-view-label">{t('queueView', 'Vue')}&nbsp;:</span>
                <select
                  value={activeTopicTab.startsWith('view:') ? activeTopicTab : 'general'}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === 'general') {
                      handleTopicTabChange(GENERAL_TAB);
                      return;
                    }
                    const view = ticketViews.find((v) => buildViewTabKey(getViewId(v)) === value);
                    if (view) handleViewTabChange(view);
                  }}
                >
                  <option value="general">{t('general', 'Général')}</option>
                  {ticketViews.map((view) => {
                    const viewId = getViewId(view);
                    if (!viewId) return null;
                    const tabKey = buildViewTabKey(viewId);
                    return (
                      <option key={tabKey} value={tabKey}>{view.name}</option>
                    );
                  })}
                </select>
              </label>
            )}
            <DisplayPreferences
              views={ticketViews}
              statuses={isEdnCustomUser
                ? EDN_STATUS_OPTIONS
                : ALL_STATUS_MAPPING.map(({ value }) => ({ value, label: getStatusLabel(value) }))}
              preferences={preferences}
              getViewId={getViewId}
              onSaved={handlePreferencesSaved}
            />
            {attentionReason && (
              <span className="queue-facet">
                {t('facetAttention', 'À traiter')}&nbsp;: {t(`dashboardInsights.flag_${attentionReason}`, attentionReason)}
                <button type="button" onClick={() => onAttentionReasonChange && onAttentionReasonChange(null)} aria-label={t('clear', 'Effacer')}>✕</button>
              </span>
            )}
            {!isEdnCustomUser && statusFilter && (
              <span className="queue-facet">
                {t('facetStatus', 'Statut')}&nbsp;: {getStatusLabel(statusFilter)}
                <button type="button" onClick={() => applyKpiStatusFilter(statusFilter)} aria-label={t('clear', 'Effacer')}>✕</button>
              </span>
            )}
            {assignmentFacet && (
              <span className="queue-facet">
                {t('facetAssignment', 'Assignation')}&nbsp;: {assignmentFacet}
                <button type="button" onClick={() => handleTopicTabChange(GENERAL_TAB)} aria-label={t('clear', 'Effacer')}>✕</button>
              </span>
            )}
            {activeTopicTab === VIP_TAB && (
              <span className="queue-facet vip">
                {t('vip', 'VIP')}
                <button type="button" onClick={() => handleTopicTabChange(GENERAL_TAB)} aria-label={t('clear', 'Effacer')}>✕</button>
              </span>
            )}
            <div className="queue-add-filter" ref={filterMenuRef}>
              <button
                type="button"
                className="queue-add-filter-trigger"
                aria-haspopup="menu"
                aria-expanded={showFilterMenu}
                onClick={() => setShowFilterMenu((prev) => !prev)}
              >
                + {t('addFilter', 'Filtre')}
              </button>
              {showFilterMenu && (
                <div className="queue-filter-menu" role="menu">
                  <span className="queue-filter-menu-title">{t('facetAssignment', 'Assignation')}</span>
                  <button type="button" role="menuitem" onClick={() => { setShowFilterMenu(false); handleTopicTabChange(ASSIGNED_TAB); }}>
                    {t('queueAssigned', 'Assignés')}
                  </button>
                  <button type="button" role="menuitem" onClick={() => { setShowFilterMenu(false); handleTopicTabChange(MY_ASSIGNED_TAB); }}>
                    {t('queueMine', 'À moi')}
                  </button>
                  {hasFaAccount && (
                    <>
                      <span className="queue-filter-menu-title">{t('vip', 'VIP')}</span>
                      <button type="button" role="menuitem" onClick={() => { setShowFilterMenu(false); handleTopicTabChange(VIP_TAB); }}>
                        {t('vip', 'VIP')}
                      </button>
                    </>
                  )}
                  {!isEdnCustomUser && (
                    <>
                      <span className="queue-filter-menu-title">{t('facetStatus', 'Statut')}</span>
                      {ALL_STATUS_MAPPING.map(({ value }) => (
                        <button
                          key={value}
                          type="button"
                          role="menuitem"
                          onClick={() => { setShowFilterMenu(false); if (statusFilter !== value) applyKpiStatusFilter(value); }}
                        >
                          {getStatusLabel(value)}
                        </button>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>
            {!isEdnCustomUser && (
              <div className="search-container queue-search">
                <input
                  id="search-input"
                  type="text"
                  value={searchFilter}
                  onChange={handleSearchFilterChange}
                  onKeyPress={handleKeyPress}
                  placeholder={t('searchQueuePlaceholder', 'Rechercher client, sujet, n° de suivi…')}
                  className="search-input"
                />
                <button
                  onClick={handleSearchSubmit}
                  className="search-submit-button"
                  title={t('search', 'Rechercher')}
                >
                  <FaSearch />
                </button>
                {searchFilter && (
                  <button
                    onClick={clearSearchFilter}
                    className="clear-search-button"
                    title={t('clearSearch', 'Effacer la recherche')}
                  >
                    ✕
                  </button>
                )}
              </div>
            )}
            {isEdnCustomUser && onCreateTicket && (
              <button onClick={onCreateTicket} className="u-btn u-btn--primary queue-right">
                <FaPlusCircle /> {t('createEdnTicket', 'Créer un ticket EDN')}
              </button>
            )}
            {canExportTickets && (
              <button
                type="button"
                onClick={() => setShowExportModal(true)}
                className={`u-btn u-btn--secondary${isEdnCustomUser ? '' : ''}`}
                title={t('ticketExport.title', 'Exporter les tickets')}
              >
                <FaDownload /> {t('ticketExport.exportButton', 'Exporter')}
              </button>
            )}
            {showExportModal && (
              <TicketExportModal onClose={() => setShowExportModal(false)} />
            )}
        </div>
      )}
      </div>

      {isEdnCustomUser && (
      <div className="table-controls">
        <div className="filters-container">
          <div className="status-filter">
              <div className="edn-status-dropdown-container" ref={ednStatusDropdownRef}>
                <button
                  type="button"
                  className={`edn-status-filter-trigger ${ednStatusFilters.length > 0 ? 'active' : ''}`}
                  onClick={() => setShowEdnStatusDropdown((prev) => !prev)}
                >
                  <span>{getEdnStatusFilterLabel()}</span>
                  <span className="edn-status-filter-caret">▾</span>
                </button>
                {showEdnStatusDropdown && (
                  <div className="edn-status-filter-menu">
                    <div className="edn-status-filter-menu-header">
                      <span>{t('status', 'Statut')}</span>
                      {ednStatusFilters.length > 0 && (
                        <button type="button" onClick={clearEdnStatusFilters}>
                          {t('clear', 'Effacer')}
                        </button>
                      )}
                    </div>
                    <div className="edn-status-filter-list">
                      {EDN_STATUS_OPTIONS.map((option) => (
                        <label key={option.value} className="edn-status-filter-item">
                          <input
                            type="checkbox"
                            checked={ednStatusFilters.includes(option.value)}
                            onChange={() => handleEdnStatusToggle(option.value)}
                          />
                          <span>{option.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
          </div>

          <div className="edn-admin-filter">
              <input
                id="edn-admin-filter"
                type="text"
                value={ednAdminFilter}
                onChange={handleEdnAdminFilterChange}
                onKeyPress={handleEdnAdminFilterKeyPress}
                onBlur={handleEdnAdminFilterSubmit}
                placeholder={t('filterByAdmin', 'Filtrer par admin')}
                className="edn-admin-input"
              />
              {ednAdminFilter && (
                <button
                  type="button"
                  onClick={clearEdnAdminFilter}
                  className="edn-admin-clear-button"
                  title={t('clearAdminFilter', 'Effacer le filtre admin')}
                >
                  ✕
                </button>
              )}
          </div>

          <div className="company-filter">
            <div className="search-container">
              <input
                id="search-input"
                type="text"
                value={searchFilter}
                onChange={handleSearchFilterChange}
                onKeyPress={handleKeyPress}
                placeholder={t('searchQueuePlaceholder', 'Rechercher client, sujet, n° de suivi…')}
                className="search-input"
              />
              <button
                onClick={handleSearchSubmit}
                className="search-submit-button"
                title={t('search', 'Rechercher')}
              >
                <FaSearch />
              </button>
              {searchFilter && (
                <button
                  onClick={clearSearchFilter}
                  className="clear-search-button"
                  title={t('clearSearch', 'Effacer la recherche')}
                >
                  ✕
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      )}

      <div className="table-scroll">
      <table>
        {/* Explicit colgroup: with table-layout fixed this is the geometry
            contract — the subject column takes all remaining space and can
            never be crushed by the fixed-width columns. */}
        <colgroup>
          {canAct && <col style={{ width: 36 }} />}
          {isEdnCustomUser && <col style={{ width: 150 }} />}
          {isEdnCustomUser && <col style={{ width: 140 }} />}
          <col style={{ width: 210 }} />
          <col /* subject: flexible */ />
          <col style={{ width: 170 }} />
          <col style={{ width: 125 }} />
          <col style={{ width: 120 }} />
          <col style={{ width: 86 }} />
          {canAct && <col style={{ width: 140 }} />}
        </colgroup>
        <thead>
          <tr>
            {canAct && (
              <th className="col-check">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleSelectAll}
                  aria-label={t('selectedCount', '{{count}} sélectionnés', { count: selectedIds.length })}
                />
              </th>
            )}
            {isEdnCustomUser && <th className="col-first">{t('topicColumn', 'Motif')}</th>}
            {isEdnCustomUser && <th className="col-admin">{t('admin', 'Admin')}</th>}
            <th className="col-customer">{t('customer', 'Client')}</th>
            <th className="col-subject">{t('subject')}</th>
            <th className="col-status">{t('status')}</th>
            <th
              className="sortable col-waiting"
              onClick={() => handleSortChange('updated_at')}
              aria-sort={sortField === 'updated_at' ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
            >
              {t('waitingOnColumn', 'En attente de')} <SortIcon field="updated_at" />
            </th>
            <th className="col-carrier">{t('carrier')}</th>
            <th
              onClick={() => handleSortChange('sentiment_score')}
              className="sortable col-score"
              aria-sort={sortField === 'sentiment_score' ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
              title={t('sentimentHint', "Sentiment estimé par l'IA d'après les messages (0–100)")}
            >
              {t('sentimentScore', 'Humeur')} <SortIcon field="sentiment_score" />
            </th>
            {canAct && <th className="col-actions" aria-label={t('assignTicket', 'Assigner')} />}
          </tr>
        </thead>
        <tbody>
          {sortedAndFilteredTickets.map((ticket) => {
            const ChannelIcon = channelIcons[ticket.channel_name];
            const statusLabel = getStatusLabel(ticket.status);
            const topicLabel = getTicketTopicLabel(ticket);
            const affectedAdminLabel = getAffectedAdminLabel(ticket);
            const attentionFlags = Array.isArray(ticket.attention_flags) ? ticket.attention_flags : [];
            const attentionTitle = attentionFlags
              .map((flag) => t(`dashboardInsights.flag_${flag}`, flag))
              .join(' · ');
            const rowClasses = [
              ticket.is_vip ? 'vip-row' : '',
              attentionFlags.length > 0 ? 'attention-row' : ''
            ].filter(Boolean).join(' ');
            const client = clientCellFor(ticket);
            const holder = holderForStatus(ticket.status);
            const waitTitle = `${t('updatedAt', 'Maj le')} ${formatDate(ticket.updated_at)} · ${t('createdAtTooltip', 'Créé le {{date}}', { date: formatDate(ticket.created_at) })}`;
            const isSelected = selectedIds.includes(ticket._id);
            return (
              <tr key={ticket._id} className={rowClasses} data-tour-demo={ticket.is_demo ? 'true' : undefined} onClick={() => onTicketClick(ticket._id)}>
                {canAct && (
                  <td className="col-check" onClick={(event) => event.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSelected(ticket._id)}
                      aria-label={ticket.subject || ticket._id}
                    />
                  </td>
                )}
                {isEdnCustomUser && (
                  <td className="tertiary-info">
                    <span className="edn-topic-pill" title={topicLabel}>
                      {topicLabel}
                    </span>
                  </td>
                )}
                {isEdnCustomUser && (
                  <td className="secondary-info edn-admin-cell" title={affectedAdminLabel}>
                    {affectedAdminLabel && affectedAdminLabel !== '-' ? (
                      <span className="admin-cell-content">
                        <span className="admin-avatar" aria-hidden="true">{getInitials(affectedAdminLabel)}</span>
                        <span className="admin-name truncate">{affectedAdminLabel}</span>
                      </span>
                    ) : (
                      <span className="muted-dash">–</span>
                    )}
                  </td>
                )}
                <td className="client-cell" title={ticket.customer_email || ticket.company_name || ''}>
                  <div className="client-lines">
                    <span className="client-primary truncate">{client.primary}</span>
                    <span className="client-secondary truncate">
                      {ChannelIcon && <ChannelIcon className="client-channel-icon" title={ticket.channel_name || ''} />}
                      {client.secondary}
                    </span>
                  </div>
                </td>
                <td className="primary-info">
                  <div className="subject-cell">
                    {attentionFlags.length > 0 && (
                      <span className="attention-flag" title={attentionTitle} aria-label={attentionTitle}>
                        <FaExclamationTriangle />
                      </span>
                    )}
                    {hasFaAccount && (
                      <span className={`cat-badge ${
                        ticket.topic === 'Avant vente' ? 'presales' :
                        ticket.topic === 'chatbot' ? 'chatbot' : 'postsales'
                      }`}>
                        {ticket.topic === 'Avant vente' ? t('presales', 'Avant-vente') :
                         ticket.topic === 'chatbot' ? t('chatbot', 'Chatbot') :
                         t('postsales', 'Après-vente')}
                      </span>
                    )}
                    <div className="subject-lines">
                      <span className="subject-text truncate" title={ticket.subject}>{ticket.subject}</span>
                      {(ticket.metadata || {}).original_subject && ticket.metadata.original_subject !== ticket.subject && (
                        <span
                          className="original-subject truncate"
                          title={`${t('originalSubject', "Objet d'origine")}: ${ticket.metadata.original_subject}`}
                        >
                          {ticket.metadata.original_subject}
                        </span>
                      )}
                    </div>
                    {renderPresenceIndicator(ticket)}
                  </div>
                </td>
                <td>
                  {renderStatusCell(ticket, statusLabel)}
                </td>
                <td className="secondary-info waiting-cell" title={waitTitle}>
                  {holder ? (
                    <span className="waiting-holder">
                      <span className="waiting-who">{HOLDER_LABELS[holder]}</span>
                      {' · '}
                      <b>{formatDurationSince(ticket.updated_at)}</b>
                    </span>
                  ) : (
                    <span className="muted-dash">—</span>
                  )}
                </td>
                <td className="secondary-info" title={ticket.carrier_name || ''}>
                  {ticket.carrier_name || <span className="muted-dash">–</span>}
                </td>
                <td>
                  {Number.isFinite(Number(ticket.sentiment_score)) ? (
                    <span className={`sentiment-score ${getSentimentScoreClass(Number(ticket.sentiment_score))}`}>
                      {Math.round(Number(ticket.sentiment_score))}
                    </span>
                  ) : (
                    <span
                      className="sentiment-score empty"
                      title={t('noSatisfactionScore', 'Pas encore de score')}
                    >
                      –
                    </span>
                  )}
                </td>
                {canAct && (
                  <td className="row-actions" onClick={(event) => event.stopPropagation()}>
                    <div className="row-actions-inner">
                      <div className="row-assign">
                        <button
                          type="button"
                          className="row-action-link"
                          onClick={() => { ensureEmployees(); setAssignMenuFor(assignMenuFor === ticket._id ? null : ticket._id); }}
                        >
                          {t('assignTicket', 'Assigner')}
                        </button>
                        {assignMenuFor === ticket._id && (
                          <div ref={assignMenuRef} className="assign-menu-anchor">
                            <AssignMenu ids={[ticket._id]} />
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        className="row-action-link row-action-summary"
                        onClick={() => { setPresenceTooltip(null); setSummaryFor({ id: ticket._id, subject: ticket.subject }); }}
                        title={t('ticketSummaryOverlay.open', 'Read the ticket summary')}
                      >
                        <FaClipboardList aria-hidden="true" /> {t('ticketSummaryOverlay.action', 'Summary')}
                      </button>
                      <button type="button" className="row-action-link" onClick={() => onTicketClick(ticket._id)}>
                        {t('openTicket', 'Ouvrir')}
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>

      {sortedAndFilteredTickets.length === 0 && isLoading && searchResults === null && (
        <div className="table-loading-state" role="status">
          {[0, 1, 2, 3, 4].map((i) => <span key={i} className="table-loading-row" aria-hidden="true" />)}
          <span className="table-loading-label">{t('loadingTicketsList', 'Chargement des tickets…')}</span>
        </div>
      )}

      {sortedAndFilteredTickets.length === 0 && !(isLoading && searchResults === null) && (
        <div className="table-empty-state">
          {searchResults !== null
            ? t('noSearchResults', 'Aucun ticket ne correspond à cette recherche.')
            : t('noTicketsInView', 'Aucun ticket dans cette vue.')}
        </div>
      )}

      {(searchResults !== null || getCurrentPagination() || canAct) && (
        <div className="table-footer">
          <div className="table-footer-left">
            {canAct && selectedIds.length > 0 ? (
              <div className="bulk-bar">
                <span className="bulk-count">{t('selectedCount', '{{count}} sélectionnés', { count: selectedIds.length })}</span>
                <div className="row-assign">
                  <button
                    type="button"
                    className="u-btn u-btn--secondary bulk-btn"
                    disabled={bulkBusy}
                    onClick={() => { ensureEmployees(); setAssignMenuFor(assignMenuFor === 'bulk' ? null : 'bulk'); }}
                  >
                    {t('assignTicket', 'Assigner')} ▾
                  </button>
                  {assignMenuFor === 'bulk' && (
                    <div ref={assignMenuRef} className="assign-menu-anchor up">
                      <AssignMenu ids={selectedIds} />
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  className="u-btn u-btn--secondary bulk-btn"
                  disabled={bulkBusy}
                  onClick={() => resolveTickets(selectedIds)}
                >
                  {t('resolveTickets', 'Résoudre')}
                </button>
                <button type="button" className="bulk-clear" onClick={() => setSelectedIds([])} aria-label={t('clear', 'Effacer')}>✕</button>
              </div>
            ) : canAct && sortedAndFilteredTickets.length > 0 ? (
              <span className="bulk-hint">
                {t('paginationTickets', '{{count}} tickets', { count: sortedAndFilteredTickets.length })} · {t('bulkHintResolve', 'actions groupées : assigner, résoudre')}
              </span>
            ) : null}
          </div>
          {(searchResults !== null || getCurrentPagination()) && renderPagination()}
        </div>
      )}
      </div>
    </div>
  );
};

export default TicketTableDashboard;
